import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/register">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Назад
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-gray-900">TACTIC SELLER</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Политика конфиденциальности
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            Редакция от 11 января 2026 года
          </p>

          {/* 1. Общие положения */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              1. Общие положения
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>
                1.1. Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок 
                обработки и защиты персональных данных пользователей сервиса Tactic Seller (далее — «Сервис»), 
                расположенного по адресу tacticseller.ru.
              </p>
              <p>
                1.2. Политика разработана в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ 
                «О персональных данных» (далее — ФЗ-152) и иными нормативными правовыми актами 
                Российской Федерации в области защиты персональных данных.
              </p>
              <p>
                1.3. Оператором персональных данных является ИП или юридическое лицо, владеющее Сервисом 
                (далее — «Оператор»).
              </p>
              <p>
                1.4. Регистрируясь в Сервисе, пользователь выражает согласие с условиями настоящей 
                Политики и даёт согласие на обработку своих персональных данных в соответствии 
                с ФЗ-152.
              </p>
              <p>
                1.5. В случае несогласия с условиями Политики пользователь должен прекратить 
                использование Сервиса.
              </p>
            </div>
          </section>

          {/* 2. Собираемые данные */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              2. Перечень обрабатываемых персональных данных
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>2.1. Оператор обрабатывает следующие категории персональных данных:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Данные, предоставляемые пользователем при регистрации:</strong>
                  <ul className="list-circle pl-4 mt-1 space-y-1">
                    <li>Адрес электронной почты (email)</li>
                    <li>Имя пользователя</li>
                    <li>Пароль (хранится в зашифрованном виде)</li>
                  </ul>
                </li>
                <li>
                  <strong>Данные, получаемые автоматически:</strong>
                  <ul className="list-circle pl-4 mt-1 space-y-1">
                    <li>IP-адрес</li>
                    <li>Данные cookies</li>
                    <li>Информация о браузере и устройстве</li>
                    <li>Дата и время доступа к Сервису</li>
                  </ul>
                </li>
                <li>
                  <strong>Данные интеграций с маркетплейсами:</strong>
                  <ul className="list-circle pl-4 mt-1 space-y-1">
                    <li>API-ключи для доступа к кабинетам продавца (Ozon, Wildberries и др.)</li>
                    <li>Данные о товарах, остатках, продажах и финансовых операциях</li>
                  </ul>
                </li>
              </ul>
              <p>
                2.2. Оператор не собирает и не обрабатывает специальные категории персональных данных, 
                касающиеся расовой, национальной принадлежности, политических взглядов, религиозных 
                или философских убеждений, состояния здоровья, интимной жизни.
              </p>
            </div>
          </section>

          {/* 3. Цели обработки */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              3. Цели обработки персональных данных
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>3.1. Оператор обрабатывает персональные данные в следующих целях:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Регистрация и идентификация пользователя в Сервисе</li>
                <li>Предоставление доступа к функциям Сервиса</li>
                <li>Связь с пользователем: отправка уведомлений, кодов подтверждения, 
                    информации об изменениях в работе Сервиса</li>
                <li>Улучшение качества Сервиса и разработка новых функций</li>
                <li>Обеспечение безопасности и предотвращение мошенничества</li>
                <li>Выполнение требований законодательства РФ</li>
                <li>Аналитика использования Сервиса (в обезличенном виде)</li>
              </ul>
            </div>
          </section>

          {/* 4. Правовые основания */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              4. Правовые основания обработки
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>4.1. Обработка персональных данных осуществляется на следующих правовых основаниях:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Согласие субъекта персональных данных</strong> (п. 1 ч. 1 ст. 6 ФЗ-152) — 
                  пользователь даёт согласие при регистрации путём проставления отметки в 
                  соответствующем поле
                </li>
                <li>
                  <strong>Исполнение договора</strong> (п. 5 ч. 1 ст. 6 ФЗ-152) — обработка необходима 
                  для предоставления услуг Сервиса согласно Пользовательскому соглашению
                </li>
                <li>
                  <strong>Законные интересы Оператора</strong> (п. 7 ч. 1 ст. 6 ФЗ-152) — обеспечение 
                  безопасности и улучшение Сервиса
                </li>
              </ul>
            </div>
          </section>

          {/* 5. Сроки хранения */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              5. Сроки хранения персональных данных
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>5.1. Персональные данные хранятся в течение следующих сроков:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Данные учётной записи</strong> — в течение всего периода использования 
                  Сервиса и 3 (три) года после удаления аккаунта или прекращения использования
                </li>
                <li>
                  <strong>Данные о транзакциях и финансовых операциях</strong> — 5 (пять) лет 
                  в соответствии с требованиями бухгалтерского и налогового законодательства
                </li>
                <li>
                  <strong>Технические данные (логи, IP-адреса)</strong> — 1 (один) год
                </li>
              </ul>
              <p>
                5.2. По истечении сроков хранения персональные данные уничтожаются или обезличиваются.
              </p>
            </div>
          </section>

          {/* 6. Права пользователя */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              6. Права субъекта персональных данных
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>6.1. В соответствии с ФЗ-152 пользователь имеет право:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Получить информацию об обработке своих персональных данных</li>
                <li>Требовать уточнения, блокирования или уничтожения персональных данных, 
                    если они являются неполными, устаревшими, неточными или незаконно полученными</li>
                <li>Отозвать согласие на обработку персональных данных</li>
                <li>Требовать удаления своей учётной записи и всех связанных данных</li>
                <li>Обжаловать действия Оператора в Роскомнадзор или в судебном порядке</li>
              </ul>
              <p>
                6.2. Для реализации своих прав пользователь может обратиться к Оператору 
                по контактным данным, указанным в разделе 9 настоящей Политики.
              </p>
              <p>
                6.3. Оператор обязан рассмотреть обращение в течение 30 (тридцати) дней 
                с момента его получения.
              </p>
            </div>
          </section>

          {/* 7. Защита данных */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              7. Меры по защите персональных данных
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>7.1. Оператор принимает необходимые организационные и технические меры для защиты 
                 персональных данных от неправомерного доступа, уничтожения, изменения, блокирования, 
                 копирования, распространения:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Использование защищённого соединения (HTTPS/TLS)</li>
                <li>Шифрование паролей с использованием современных алгоритмов хеширования</li>
                <li>Шифрование API-ключей маркетплейсов при хранении</li>
                <li>Защита от DDoS-атак (Cloudflare)</li>
                <li>Ограничение доступа к персональным данным (только уполномоченные сотрудники)</li>
                <li>Регулярное резервное копирование</li>
                <li>Мониторинг безопасности и выявление угроз</li>
              </ul>
            </div>
          </section>

          {/* 8. Передача данных */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              8. Передача персональных данных третьим лицам
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>8.1. Оператор не продаёт и не передаёт персональные данные третьим лицам 
                 для маркетинговых целей.</p>
              <p>8.2. Персональные данные могут быть переданы третьим лицам в следующих случаях:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>С согласия пользователя</li>
                <li>По требованию уполномоченных государственных органов РФ в случаях, 
                    предусмотренных законодательством</li>
                <li>Для исполнения договорных обязательств (например, сервисы отправки email)</li>
              </ul>
              <p>8.3. При привлечении третьих лиц для обработки данных Оператор обеспечивает 
                 соблюдение ими требований конфиденциальности.</p>
              <p>8.4. Используемые сервисы-обработчики:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Resend</strong> — отправка email-уведомлений</li>
                <li><strong>Cloudflare</strong> — защита от атак и CDN</li>
                <li><strong>MongoDB Atlas</strong> — хранение данных (серверы в ЕС)</li>
              </ul>
            </div>
          </section>

          {/* 9. Контакты */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              9. Контактная информация Оператора
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>9.1. По всем вопросам, связанным с обработкой персональных данных, 
                 пользователь может обратиться:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Email:</strong> support@tacticseller.ru</li>
                <li><strong>Адрес:</strong> Российская Федерация (точный адрес уточняется)</li>
              </ul>
              <p>9.2. Ответ на обращение будет направлен на email, указанный пользователем, 
                 в течение 30 дней.</p>
            </div>
          </section>

          {/* 10. Изменения */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              10. Изменение Политики конфиденциальности
            </h2>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>10.1. Оператор вправе вносить изменения в настоящую Политику.</p>
              <p>10.2. Новая редакция Политики вступает в силу с момента её размещения на сайте, 
                 если иное не предусмотрено новой редакцией.</p>
              <p>10.3. Продолжение использования Сервиса после внесения изменений означает 
                 согласие пользователя с новой редакцией Политики.</p>
              <p>10.4. Пользователь обязуется самостоятельно следить за изменениями Политики.</p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-xs text-gray-500 text-center">
              © 2026 Tactic Seller. Все права защищены.<br />
              Настоящая Политика конфиденциальности составлена в соответствии с требованиями 
              Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных».
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
